/**
 * Plan Gating Utility
 * Manages user subscription and trial access levels across the application
 */

import llStorage from '../llStorage';

// Plan hierarchy definition
export const planHierarchy = { free: 0, plus: 1, pro: 2, enterprise: 3 };

/**
 * Normalize plan name to ensure it exists in hierarchy
 * @param {string} plan - Plan name to normalize
 * @returns {string} Normalized plan name (defaults to 'free' if invalid)
 */
export const normalizePlan = (plan) => {
  return plan && planHierarchy[plan] !== undefined ? plan : 'free';
};

/**
 * Check if a date range is currently active
 * @param {string|Date} start - Start date
 * @param {string|Date} end - End date
 * @returns {boolean} True if range is active
 */
export const isRangeActive = (start, end) => {
  const now = new Date();
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  // If both missing, treat as active (no date restriction)
  if (!s && !e) return true;
  return (!s || now >= s) && (!e || now <= e);
};

/**
 * Check if user's subscription is currently active
 * @param {Object} userDetails - User details object
 * @returns {boolean} True if subscription is active
 */
export const isSubscriptionActive = (userDetails) => {
  return (
    userDetails.userPlanSubscriptionStatus === 'active' &&
    isRangeActive(userDetails.userPlanSubscriptionStartDate, userDetails.userPlanSubscriptionEndDate)
  );
};

/**
 * Check if user's trial is currently active
 * @param {Object} userDetails - User details object
 * @returns {boolean} True if trial is active
 */
export const isTrialActive = (userDetails) => {
  return (
    !!userDetails.trialPlanUsed &&
    isRangeActive(userDetails.trialStartDate, userDetails.trialEndDate)
  );
};

/**
 * Get current access level based on subscription and trial
 * @param {Object} userDetails - User details object
 * @returns {number} Access level (0-3)
 */
export const currentAccessLevel = (userDetails) => {
  const subLevel = isSubscriptionActive(userDetails)
    ? planHierarchy[normalizePlan(userDetails.userPlanSubscription)]
    : 0;
  const trialLevel = isTrialActive(userDetails)
    ? planHierarchy[normalizePlan(userDetails.trialPlanUsed)]
    : 0;
  return Math.max(subLevel, trialLevel);
};

/**
 * Get gate configuration for a required plan level
 * @param {string} requiredPlan - Required plan level ('plus', 'pro', 'enterprise')
 * @param {Object} userDetails - User details object
 * @param {string} lang - Language code for translations
 * @param {Function} t - Translation function
 * @returns {Object} Gate configuration with visibility, disabled state, access info, and badge text
 */
export const getGate = (requiredPlan, userDetails, lang = 'en', t = (key) => key) => {
  const req = normalizePlan(requiredPlan);
  const reqLevel = planHierarchy[req];
  const hasAccess = currentAccessLevel(userDetails) >= reqLevel;
  const visible = hasAccess || userDetails.showHiddenFeatures;
  const disabled = !hasAccess;
  
  let badgeText = '';
  if (!hasAccess) {
    badgeText = `🔒 ${t('settings.' + req, lang)}`;
  } else if (
    isTrialActive(userDetails) &&
    planHierarchy[normalizePlan(userDetails.trialPlanUsed)] >= reqLevel &&
    !(isSubscriptionActive(userDetails) && planHierarchy[normalizePlan(userDetails.userPlanSubscription)] >= reqLevel)
  ) {
    const dateStr = userDetails.trialEndDate
      ? new Date(userDetails.trialEndDate).toISOString().slice(0, 10)
      : '';
    badgeText = `${t('settings.trial', lang) || 'Trial'}${dateStr ? ' • ' + dateStr : ''}`;
  } else if (isSubscriptionActive(userDetails)) {
    const dateStr = userDetails.userPlanSubscriptionEndDate
      ? new Date(userDetails.userPlanSubscriptionEndDate).toISOString().slice(0, 10)
      : '';
    badgeText = `${t('settings.active', lang) || 'Active'}${dateStr ? ' • ' + dateStr : ''}`;
  }
  
  return { visible, disabled, hasAccess, badgeText, requiredPlan: req };
};

/**
 * Compute all gates and save to localStorage
 * @param {Object} userDetails - User details object
 * @param {string} lang - Language code
 * @param {Function} t - Translation function
 * @returns {Object} Object with all gates (plus, pro, enterprise)
 */
export const computeAndSaveGates = (userDetails, lang = 'en', t = (key) => key) => {
  const gates = {
    plus: getGate('plus', userDetails, lang, t),
    pro: getGate('pro', userDetails, lang, t),
    enterprise: getGate('enterprise', userDetails, lang, t),
    accessLevel: currentAccessLevel(userDetails),
    subscription: {
      plan: userDetails.userPlanSubscription,
      status: userDetails.userPlanSubscriptionStatus,
      isActive: isSubscriptionActive(userDetails),
      startDate: userDetails.userPlanSubscriptionStartDate,
      endDate: userDetails.userPlanSubscriptionEndDate,
    },
    trial: {
      plan: userDetails.trialPlanUsed,
      isActive: isTrialActive(userDetails),
      startDate: userDetails.trialStartDate,
      endDate: userDetails.trialEndDate,
    },
    showHiddenFeatures: userDetails.showHiddenFeatures || false,
  };
  
  // Save to localStorage
  llStorage.setItem('planGates', JSON.stringify(gates));
  
  return gates;
};

/**
 * Load gates from localStorage
 * @returns {Object|null} Gates object or null if not found
 */
export const loadGatesFromStorage = () => {
  try {
    const stored = llStorage.getItem('planGates');
    return stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error('Error loading gates from storage:', err);
    return null;
  }
};

/**
 * Clear gates from localStorage
 */
export const clearGatesFromStorage = () => {
  llStorage.removeItem('planGates');
};

/**
 * Check if user has access to a specific feature
 * @param {string} requiredPlan - Required plan level
 * @param {Object} gates - Gates object (from storage or computed)
 * @returns {boolean} True if user has access
 */
export const hasFeatureAccess = (requiredPlan, gates = null) => {
  if (!gates) {
    gates = loadGatesFromStorage();
  }
  if (!gates) return false;
  
  const reqLevel = planHierarchy[normalizePlan(requiredPlan)];
  return gates.accessLevel >= reqLevel;
};

/**
 * Get specific gate from storage or return a default locked gate
 * @param {string} requiredPlan - Required plan level ('plus', 'pro', 'enterprise')
 * @returns {Object} Gate configuration
 */
export const getGateFromStorage = (requiredPlan) => {
  const gates = loadGatesFromStorage();
  if (!gates) {
    // Return locked gate as default
    return {
      visible: false,
      disabled: true,
      hasAccess: false,
      badgeText: `🔒 ${requiredPlan}`,
      requiredPlan: normalizePlan(requiredPlan),
    };
  }
  
  return gates[requiredPlan] || {
    visible: false,
    disabled: true,
    hasAccess: false,
    badgeText: `🔒 ${requiredPlan}`,
    requiredPlan: normalizePlan(requiredPlan),
  };
};
